import { useState } from 'react'
import { Icon } from '@iconify/react'
import FormLoader from '../../../../components/loader/FormLoader'
import Modal from '../../../../components/modal/Modal'
import { put } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import { DOCUMENT_CHECK_LABELS } from '../shipperIds/shared'
import '../customers/CustomerModals.scss'

type Mode = 'off' | 'warn' | 'required'

const MODE_HELP: { [key in Mode]: string } = {
  off: 'API bookings never need a shipper ID.',
  warn: 'API bookings without a shipper ID still go through, but the response warns the business. Set a date and they are refused from that day on, with no further action needed.',
  required:
    'API bookings without a shipper ID are refused immediately. Give the business notice first.',
}

// Automated check of the ID image on the business's shipper ID uploads.
// Independent of `mode` (which is about bookings).
const DOCUMENT_CHECK_HELP: { [key in Mode]: string } = {
  off: 'ID images are not checked automatically.',
  warn: 'Every upload is accepted; the automated check result is returned to the business with it.',
  required:
    'ID images that fail the automated check (wrong document, unreadable, number or name mismatch) are refused with a 422.',
}

// yyyy-mm-dd for <input type='date'>, in local time.
const toDateInput = (value?: string | Date | null) => {
  if (!value) return ''
  const d = new Date(value)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function IdentityEnforcementModal({
  businessId,
  orgName,
  current,
  onSuccess,
  onClose,
}: any) {
  const { notify } = useGlobalContext()
  const [mode, setMode] = useState<Mode>(current?.mode || 'off')
  const [requiredFrom, setRequiredFrom] = useState(
    toDateInput(current?.requiredFrom),
  )
  const storedDocumentCheck: Mode = current?.documentCheck || 'off'
  const [documentCheck, setDocumentCheck] = useState<Mode>(storedDocumentCheck)
  const [loading, setLoading] = useState(false)

  const tomorrow = toDateInput(new Date(Date.now() + 24 * 60 * 60 * 1000))

  function save() {
    setLoading(true)
    put(`admin/business/${businessId}/identity-enforcement`, {
      mode,
      // Start of the chosen day, local time — the API needs a future instant.
      requiredFrom:
        mode === 'warn' && requiredFrom
          ? new Date(`${requiredFrom}T00:00:00`).toISOString()
          : null,
      // Only when changed — omitted means "leave as is" on the API.
      ...(documentCheck !== storedDocumentCheck ? { documentCheck } : {}),
    })
      .then((result: any) => {
        if (result.success) {
          notify('success', result.message)
          const stored = result.data.identityEnforcement?.documentCheck || 'off'
          const effective = result.data.effectiveDocumentCheck
          if (effective && effective !== stored) {
            notify(
              'warn',
              `Document check is saved as "${DOCUMENT_CHECK_LABELS[stored] || stored}" but is currently "${DOCUMENT_CHECK_LABELS[effective] || effective}" on this server${
                effective === 'off'
                  ? ' (automated document review is switched off)'
                  : ' (sandbox always warns)'
              }.`,
            )
          }
          onSuccess(result.data.identityEnforcement)
          onClose()
        } else {
          notify('error', result.message)
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  return (
    <Modal position='center'>
      <div className='customer-modal'>
        <div className='modal-header'>
          <div className='modal-icon icon-green'>
            <Icon icon='mdi:card-account-details-outline' />
          </div>
          <h2>Shipper ID Requirement</h2>
          <p>
            Whether {orgName || 'this business'}'s API bookings must include a
            registered shipper ID (customerIdentityId).
          </p>
        </div>

        <div className='form-group'>
          <label>Setting</label>
          <select value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
            <option value='off'>Not required</option>
            <option value='warn'>Warn, then require from a date</option>
            <option value='required'>Required now</option>
          </select>
        </div>

        {mode === 'warn' && (
          <div className='form-group'>
            <label>Required from (optional)</label>
            <input
              type='date'
              min={tomorrow}
              value={requiredFrom}
              onChange={(e) => setRequiredFrom(e.target.value)}
            />
          </div>
        )}

        <div className={`status-indicator ${mode === 'required' ? 'status-off' : 'status-on'}`}>
          <div className='dot' />
          <p>{MODE_HELP[mode]}</p>
        </div>

        <div className='form-group'>
          <label>Automated ID image check</label>
          <select
            value={documentCheck}
            onChange={(e) => setDocumentCheck(e.target.value as Mode)}
          >
            <option value='off'>Off</option>
            <option value='warn'>Warn — accept and report issues</option>
            <option value='required'>Required — refuse failing images</option>
          </select>
        </div>

        <div
          className={`status-indicator ${documentCheck === 'required' ? 'status-off' : 'status-on'}`}
        >
          <div className='dot' />
          <p>{DOCUMENT_CHECK_HELP[documentCheck]}</p>
        </div>

        {loading ? (
          <FormLoader position='center' />
        ) : (
          <div className='modal-actions'>
            <button className='btn-cancel' onClick={onClose}>
              Cancel
            </button>
            <button
              className={mode === 'required' ? 'btn-danger' : 'btn-proceed'}
              onClick={save}
              disabled={mode === 'warn' && !!requiredFrom && requiredFrom < tomorrow}
            >
              Save
            </button>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default IdentityEnforcementModal
